// Apps → Integrations (owners and admins change it, members see it; docs/integrations.md): which outside services this server's
// apps may read from, and the server's own API keys for them. Keys are write-only: once saved, only their last four
// characters come back. Test makes one real call with the server's settings.
import { useEffect, useState } from "react";
import type { IntegrationInfo, IntegrationSetting, ServerSummary } from "@bloom/shared";
import { api } from "../api.ts";
import "./server-settings.css";
import type { Member } from "@bloom/shared";

interface Settings {
  catalog: IntegrationInfo[];
  settings: IntegrationSetting[];
  usedBy: { slug: string; name: string; icon: string; integrations: string[] }[];
  canEdit: boolean;
  keysAvailable: boolean;
}

const when = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });

export function IntegrationSettings({ server, members, live }: { server: ServerSummary; members: Member[]; live: IntegrationSetting[] }) {
  const [data, setData] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    try {
      setData(await api<Settings>(`/servers/${server.id}/integrations`));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  // Reload when someone else changes them (the room's "integrations" event updates `live`).
  const liveKey = live.map((s) => `${s.id}:${s.enabled}:${s.hasKey}`).join(",");
  useEffect(() => {
    void load();
  }, [server.id, liveKey]);
  if (!data) return error ? <p className="error" role="alert">{error}</p> : <p className="muted">Loading…</p>;
  return (
    <>
      <p className="setting-help" style={{ margin: "0 0 12px" }}>
        Outside services this server's apps can read from: weather, stock quotes, definitions. Apps never reach the web themselves; Bloom makes each call for them, with this server's own keys, and only to services on this list. Everything starts off.
      </p>
      <ul className="int-list" aria-label="Integrations">
        {data.catalog.map((i) => (
          <IntegrationRow
            key={i.id}
            server={server}
            info={i}
            setting={data.settings.find((s) => s.id === i.id)!}
            usedBy={data.usedBy.filter((a) => a.integrations.includes(i.id))}
            canEdit={data.canEdit}
            keysAvailable={data.keysAvailable}
            members={members}
            onChanged={load}
          />
        ))}
      </ul>
      <p className="setting-help" style={{ marginTop: 14 }}>More are on the way. Only official, free services are listed; nothing here signs in to anyone's own account.</p>
    </>
  );
}

function IntegrationRow({ server, info, setting, usedBy, canEdit, keysAvailable, members, onChanged }: {
  server: ServerSummary;
  info: IntegrationInfo;
  setting: IntegrationSetting;
  usedBy: Settings["usedBy"];
  canEdit: boolean;
  keysAvailable: boolean;
  members: Member[];
  onChanged: () => Promise<void>;
}) {
  const [key, setKey] = useState("");
  const [editingKey, setEditingKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);
  const keyed = info.auth.type === "api_key";
  const put = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    setTest(null);
    try {
      await api(`/servers/${server.id}/integrations/${info.id}`, { method: "PUT", body });
      await onChanged();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const runTest = async () => {
    setBusy(true);
    setTest(null);
    try {
      const r = await api<{ ok: boolean; message?: string }>(`/servers/${server.id}/integrations/${info.id}/test`, { method: "POST" });
      setTest(r.ok ? { ok: true, text: "Working: it answered." } : { ok: false, text: r.message ?? "It didn't answer." });
    } catch (e) {
      setTest({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };
  const status = !setting.enabled ? "Off" : setting.ready ? (setting.keyUnreadable ? "Key needs re-entering" : "On") : "Needs a key";
  const tone = !setting.enabled ? "" : setting.ready && !setting.keyUnreadable ? " on" : " warn";
  const by = setting.updatedBy ? members.find((m) => m.id === setting.updatedBy)?.name ?? "someone" : null;
  const showKeyForm = keyed && canEdit && (editingKey || !setting.hasKey);
  return (
    <li className="int-row" data-integration={info.id}>
      <div className="int-head">
        <span className="chset-app-icon" aria-hidden="true">{info.icon}</span>
        <div className="int-text">
          <div className="int-name">
            {info.name} <span className={`int-status${tone}`}>{status}</span>
          </div>
          <div className="muted int-desc">{info.description}</div>
          <div className="muted int-meta">
            {keyed ? "Needs a free API key" : "Free, no key needed"}
            {" · "}
            <a href={info.terms} target="_blank" rel="noreferrer">Terms</a>
            {setting.calls7d ? ` · ${setting.calls7d.toLocaleString()} call${setting.calls7d === 1 ? "" : "s"} this week${setting.errors7d ? `, ${setting.errors7d} failed` : ""}` : ""}
          </div>
        </div>
        {canEdit && (
          <input
            type="checkbox"
            className="ss-check"
            role="switch"
            aria-label={`${info.name} on`}
            checked={setting.enabled}
            disabled={busy}
            onChange={(e) => void put({ enabled: e.target.checked })}
          />
        )}
      </div>
      {keyed && (
        <div className="int-key">
          {setting.hasKey && !editingKey ? (
            <div className="int-key-line">
              <span className="int-key-hint" aria-label="Saved key">••••{setting.keyHint}</span>
              <span className="muted">{by && setting.updatedAt ? `saved by ${by}, ${when.format(setting.updatedAt)}` : "saved"}</span>
              {canEdit && (
                <span className="int-key-actions">
                  <button type="button" className="btn btn-small" disabled={busy} onClick={() => setEditingKey(true)}>Replace</button>
                  <button type="button" className="btn btn-small btn-ghost" disabled={busy} onClick={() => void put({ key: null })}>Remove</button>
                </span>
              )}
            </div>
          ) : !canEdit ? (
            <span className="muted">No key yet.</span>
          ) : null}
          {showKeyForm && (
            <form
              className="int-key-form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!key.trim()) return;
                const enable = !setting.enabled;
                if (await put({ key: key.trim(), ...(enable ? { enabled: true } : {}) })) {
                  setKey("");
                  setEditingKey(false);
                }
              }}
            >
              <input
                className="input"
                type="password"
                autoComplete="off"
                spellCheck={false}
                aria-label={`${info.name} API key`}
                placeholder={keysAvailable ? `Paste this server's ${info.name} key` : "Keys can't be saved yet"}
                disabled={!keysAvailable || busy}
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
              <button className="btn btn-small btn-primary" type="submit" disabled={!key.trim() || busy}>Save key</button>
              {editingKey && <button className="btn btn-small btn-ghost" type="button" onClick={() => { setEditingKey(false); setKey(""); }}>Cancel</button>}
            </form>
          )}
          {showKeyForm && info.auth.type === "api_key" && (
            <p className="setting-help int-help">
              {info.auth.help} <a href={info.auth.signup} target="_blank" rel="noreferrer">Get a free key ↗</a>
            </p>
          )}
        </div>
      )}
      <div className="int-foot">
        {usedBy.length > 0 ? (
          <span className="muted">Used by {usedBy.map((a) => `${a.icon} ${a.name}`).join(", ")}</span>
        ) : (
          <span className="muted">No apps use it yet</span>
        )}
        {canEdit && setting.ready && (
          <button type="button" className="btn btn-small" disabled={busy} onClick={() => void runTest()}>Test</button>
        )}
      </div>
      {test && <p className={test.ok ? "int-test ok" : "int-test error"} role="status">{test.text}</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </li>
  );
}
